import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Modal, Row, Table, Avatar, Input, Spin, message } from "antd";
import { DeleteOutlined, EditOutlined, FolderAddTwoTone } from "@ant-design/icons";
import { connect } from "react-redux";
import {
  createModule,
  deleteModule,
  fetchModule,
  fetchModules,
  setModuleError,
  updateModule,
} from "../../store/actions/moduleActions";
import {
  clearSearchResults,
  searchTrack,
} from "../../store/actions/trackActions";
import { getVideos } from "../../store/actions/videoActions";
import {
  getBase64,
  uploadOtherFilesToCloudflare,
} from "../../utils/uploadFileToCloudflare";
import CreateUpdateForm from "./CreateUpdateForm";
import DeleteModule from "./DeleteModule";

const { Search } = Input;

const Modules = ({
  modules,
  moduleError,
  createModule,
  updateModule,
  fetchModules,
  deleteModule,
  getVideos,
  searchTrack,
  searchResult,
  searchError,
  clearSearchResults,
  videos,
}) => {
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("createTrack");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailURL, setThumbnailURL] = useState(null);
  const [videoIds, setVideoIds] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchedResults, setSearchedResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalModules, setTotalModules] = useState(0);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchModules({ page: currentPage, perPage: pageSize }), getVideos()]);
      } catch (error) {
        message.error("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [fetchModules, getVideos, currentPage, pageSize]);

  useEffect(() => {
    if (searchResult && searchText) {
      setSearchedResults(searchResult.modules || []);
    }

    if (!searchText || searchText === "") {
      setSearchedResults([]);
    }
  }, [searchResult, searchText]);

  useEffect(() => {
    console.log('Modules component - modules state:', modules);
    console.log('Modules component - modules type:', typeof modules);
    console.log('Modules component - modules.data:', modules?.data);
    console.log('Modules component - modules.pagination:', modules?.pagination);
    
    if (modules && modules.pagination) {
      setTotalModules(modules.pagination.total || 0);
      setCurrentPage(modules.pagination.page || 1);
      setPageSize(modules.pagination.perPage || 10);
    }
  }, [modules]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const openModal = (modalType, record) => {
    setIsModalVisible(true);
    if (record) {
      setType(modalType);
      setName(record.name);
      setDescription(record.description);
      setModuleId(record.id);
      setThumbnailURL(record.thumbnail);
      setVideoIds(
        record.videos && record.videos.length > 0
          ? record.videos.map((video) => video.id)
          : []
      );
    }
  };

  const clearState = () => {
    setVideoIds([]);
    setName("");
    setDescription("");
    setLoading(false);
    setThumbnailURL(null);
    setThumbnail(null);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    clearState();
  };

  const handleInputChange = (event, eventType) => {
    if (eventType === "name") {
      setName(event.target.value);
    }

    if (eventType === "description") {
      setDescription(event.target.value);
    }
  };

  const handleSelectChanger = (value) => {
    setVideoIds(value);
  };

  const handleUpload = (upload) => {
    getBase64(upload.file.originFileObj, (imageUrl) => {
      setThumbnail(upload);
      setThumbnailURL(imageUrl);
    });
  };

  const handleOk = async () => {
    setLoading(true);

    if (["createModule", "createTrack", "editModule"].includes(type) && !name) {
      setFormError("Please provide a name for this module");
      return false;
    }

    if (type === "createModule" || type === "createTrack") {
      let fileURL = "";
      if (thumbnail) {
        fileURL = await uploadOtherFilesToCloudflare(
          thumbnail.originFileObj,
          "thumbnails",
        );
      }
      const finalPayload = {
        name,
        description,
        videos: videoIds,
        thumbnail: fileURL,
      };
      await createModule(finalPayload);
    }

    if (type === "editModule") {
      let fileURL = "";
      if (thumbnail) {
        fileURL = await uploadOtherFilesToCloudflare(
          thumbnail.originFileObj, 
          "thumbnails",
        );
      } else {
        fileURL = thumbnailURL;
      }
      const finalPayload = {
        name,
        description,
        videos: videoIds,
        thumbnail: fileURL,
      };

      await updateModule(finalPayload, moduleId);
    }

    if (type === "deleteModule") {
      await deleteModule(moduleId);
    }

    clearState();
    setLoading(false);
    setIsModalVisible(false);
    await fetchModules({ page: currentPage, perPage: pageSize });
  };

  const onSearch = async (value) => {
    if (value) {
      clearSearchResults();
      await searchTrack(value);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (!value) {
      setSearchedResults([]);
      clearSearchResults();
    }
  };

  const columns = [
    {
      title: "Thumbnail",
      dataIndex: "thumbnail",
      key: "thumbnail",
      render: (_, record) => (
        <div>
          <Avatar size="large" shape="square" src={record.thumbnail || null} alt="" />
        </div>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: "Videos",
      dataIndex: "videos",
      key: "videos",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) =>
        a.videos && b.videos ? a.videos.length - b.videos.length : false,
      render: (_, record) => (
        <div>
          {record.videos && record.videos.length > 0
            ? record.videos.slice(0, 3).map((video) => {
                return <Button key={video.id} style={{ marginRight: 5 }}>{video.name}</Button>;
              })
            : null}
        </div>
      ),
    },
    {
      title: "CreatedAt",
      dataIndex: "createdAt",
      key: "createdAt",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Action",
      dataIndex: "",
      key: "x",
      render: (_, record) => (
        <div>
          <Row gutter={4}>
            <Col>
              <EditOutlined
                style={{ color: "#1890ff" }}
                onClick={() => openModal("editModule", record)}
              />
            </Col>

            <Col>
              <DeleteOutlined
                style={{ color: "red" }}
                onClick={() => openModal("deleteModule", record)}
              />
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <div>
      {moduleError && typeof moduleError === "string" ? (
        <div>
          <Alert type="error" message={moduleError} />
        </div>
      ) : null}

      <div>
        <h1 style={{ marginBottom: 0 }}>Modules section</h1>
        <small>Modules section</small>
      </div>

      <div style={{ marginTop: 20 }}>
        <Row gutter={4}>
          <Col>
            <Button
              icon={<FolderAddTwoTone />}
              type="primary"
              size="middle"
              onClick={() => openModal("createModule")}
            >
              Create module
            </Button>
          </Col>

          <Col>
            <Search
              value={searchText}
              placeholder="Search here.."
              onSearch={onSearch}
              style={{ width: 200 }}
              onChange={handleSearchChange}
            />
          </Col>
        </Row>
      </div>

      <div style={{ marginTop: 20 }}>
        <Spin spinning={loading} tip="Loading modules...">
          <Table
            columns={columns}
            dataSource={(() => {
              const dataSource = searchText && searchedResults.length > 0
                ? searchedResults.map(item => ({ ...item, key: item.id }))
                : modules && modules.data && Array.isArray(modules.data)
                ? modules.data.map(item => ({ ...item, key: item.id }))
                : [];
              console.log('Table dataSource:', dataSource);
              console.log('Table dataSource length:', dataSource.length);
              return dataSource;
            })()}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalModules,
              onChange: handlePageChange,
              onShowSizeChange: handlePageSizeChange,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
            }}
          />
        </Spin>
      </div>

      <Modal
        title={
          type === "createModule"
            ? "Create module"
            : type === "editModule"
            ? "Edit module"
            : type === "deleteModule"
            ? "Delete module"
            : "Insert something here"
        }
        open={isModalVisible}
        onCancel={closeModal}
        footer={[
          <Button key="back" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger={type === "deleteModule"}
            loading={loading}
            onClick={handleOk}
          >
            {type === "createModule"
              ? "Create"
              : type === "editModule"
              ? "Update"
              : type === "deleteModule"
              ? "Delete"
              : "Done"}
          </Button>,
        ]}
      >
        {type === "deleteModule" ? (
          <DeleteModule name={name} />
        ) : (
          <CreateUpdateForm
            name={name}
            description={description}
            handleInputChange={handleInputChange}
            formError={formError}
            moduleError={moduleError}
            videos={videos}
            videoIds={videoIds}
            handleSelectChanger={handleSelectChanger}
            thumbnail={thumbnail}
            thumbnailURL={thumbnailURL}
            handleUpload={handleUpload}
          />
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authLoaded: state.firebase.auth.isLoaded,
  modules: state.modules.modules,
  singleModule: state.modules.singleModule,
  moduleError: state.modules.moduleError,
  videos: state.videos.videos,
  searchResult: state.tracks.searchResult,
  searchError: state.tracks.searchError,
});

export default connect(mapStateToProps, {
  createModule,
  updateModule,
  fetchModule,
  fetchModules,
  deleteModule,
  setModuleError,
  getVideos,
  searchTrack,
  clearSearchResults,
})(Modules);
